import { useCreateLot, useLots, useRegisterMovement } from '@/api/hooks';
import { DeleteLotModal } from '@/components/lots/DeleteLotModal';
import { LotAnimalPicker } from '@/components/lots/LotAnimalPicker';
import { LotRowActionsMenu } from '@/components/lots/LotRowActionsMenu';
import { MovementFormModal } from '@/components/movements/MovementFormModal';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { todayIso } from '@/lib/format';
import { LOT_TYPES, lotTypeLabel } from '@/lib/lotTypes';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { ClickableTableRow } from '@/components/ui/ClickableTableRow';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { canManage } from '@/lib/rbac';
import type { Lot } from '@/types/api';
import { Layers, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export { lotTypeLabel };

export function LotsTable({
  lots,
  propertyName,
  canDelete = false,
  onRegisterEvent,
  onRegisterMovement,
  onDelete,
}: {
  lots: Lot[];
  propertyName: string;
  canDelete?: boolean;
  onRegisterEvent: (lot: Lot) => void;
  onRegisterMovement: (lot: Lot) => void;
  onDelete?: (lot: Lot) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/60">
            <th className="px-5 py-3 font-medium">Nome</th>
            <th className="px-5 py-3 font-medium">Código</th>
            <th className="px-5 py-3 font-medium">Tipo</th>
            <th className="px-5 py-3 font-medium">Animais</th>
            <th className="px-5 py-3 font-medium">Status</th>
            <th className="px-5 py-3 font-medium w-12" aria-hidden />
            <th className="px-3 py-3 font-medium w-12" aria-hidden />
          </tr>
        </thead>
        <tbody className="divide-y divide-manejai-sand/60">
          {lots.map((lot) => (
            <ClickableTableRow
              key={lot.id}
              to={`/animais?lote=${lot.id}`}
              label={`Ver animais do lote ${lot.name}`}
              extraActions={
                <LotRowActionsMenu
                  lot={lot}
                  onRegisterEvent={onRegisterEvent}
                  onRegisterMovement={onRegisterMovement}
                  onDelete={onDelete}
                  canDelete={canDelete}
                />
              }
            >
              <td className="px-5 py-3 font-medium text-manejai-forest">{lot.name}</td>
              <td className="px-5 py-3 text-manejai-bark">{lot.code ?? '—'}</td>
              <td className="px-5 py-3">{lotTypeLabel(lot.lot_type)}</td>
              <td className="px-5 py-3">{lot.active_animals_count ?? 0}</td>
              <td className="px-5 py-3">
                <Badge tone={lot.is_active ? 'success' : 'default'}>
                  {lot.is_active ? 'Ativo' : 'Inativo'}
                </Badge>
              </td>
            </ClickableTableRow>
          ))}
        </tbody>
      </table>
      <div className="sr-only">Lotes em {propertyName}</div>
    </div>
  );
}

export function LotsTableWithActions({
  lots,
  propertyName,
  propertyId,
  footer,
}: {
  lots: Lot[];
  propertyName: string;
  propertyId: number;
  footer?: React.ReactNode;
}) {
  const lotActions = useLotRowActions(propertyId);

  return (
    <>
      <LotsTable
        lots={lots}
        propertyName={propertyName}
        canDelete={lotActions.canDelete}
        onRegisterEvent={lotActions.registerEvent}
        onRegisterMovement={lotActions.registerMovement}
        onDelete={lotActions.canDelete ? lotActions.deleteLot : undefined}
      />
      {footer}
      {lotActions.modals}
    </>
  );
}

function useLotRowActions(propertyId: number) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user && canManage(user.role);
  const [actionLot, setActionLot] = useState<Lot | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Lot | null>(null);
  const [movementOpen, setMovementOpen] = useState(false);

  function registerEvent(lot: Lot) {
    navigate(`/pesagem-lote?lote=${lot.id}`);
  }

  function registerMovement(lot: Lot) {
    setActionLot(lot);
    setMovementOpen(true);
  }

  function closeMovement() {
    setMovementOpen(false);
    setActionLot(null);
  }

  const modals = (
    <>
      <MovementFormModal
        open={movementOpen}
        onClose={closeMovement}
        propertyId={propertyId}
        presetLotId={actionLot?.id}
        presetLotName={actionLot?.name}
      />
      <DeleteLotModal
        lot={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );

  return {
    canDelete: !!canDelete,
    registerEvent,
    registerMovement,
    deleteLot: setDeleteTarget,
    modals,
  };
}

export function PropertyLotsPanel({
  propertyId,
  propertyName,
}: {
  propertyId: number;
  propertyName: string;
}) {
  const { data, isLoading } = useLots(propertyId);
  const [open, setOpen] = useState(false);

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display font-semibold text-manejai-forest">Lotes</h2>
          <p className="text-sm text-manejai-bark/60 mt-0.5">
            Locais dentro de <strong>{propertyName}</strong> — pastos, currais, enfermaria…
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          Novo lote
        </Button>
      </CardHeader>

      {isLoading ? (
        <CardBody className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 rounded-xl bg-manejai-sand/40 animate-pulse" />
          ))}
        </CardBody>
      ) : (data?.lots.length ?? 0) === 0 ? (
        <CardBody>
          <EmptyState
            icon={Layers}
            title="Nenhum lote nesta fazenda"
            description="Cadastre o primeiro pasto ou curral desta fazenda."
            action={
              <Button size="sm" onClick={() => setOpen(true)}>
                Cadastrar lote
              </Button>
            }
          />
        </CardBody>
      ) : (
        <>
          <LotsTableWithActions
            lots={data!.lots}
            propertyName={propertyName}
            propertyId={propertyId}
            footer={
              <div className="px-5 py-3 border-t border-manejai-sand/60 text-xs text-manejai-bark/50">
                {data?.meta.total} lote(s) em {propertyName}
              </div>
            }
          />
        </>
      )}

      <LotFormModal
        open={open}
        onClose={() => setOpen(false)}
        propertyId={propertyId}
        propertyName={propertyName}
      />
    </Card>
  );
}

export function LotFormModal({
  open,
  onClose,
  propertyId,
  propertyName,
  defaultLotType = 'pasture',
}: {
  open: boolean;
  onClose: () => void;
  propertyId: number;
  propertyName: string;
  defaultLotType?: string;
}) {
  const createLot = useCreateLot(propertyId);
  const transfer = useRegisterMovement('transfer');
  const [error, setError] = useState('');
  const [selectedAnimalIds, setSelectedAnimalIds] = useState<number[]>([]);
  const [form, setForm] = useState({
    name: '',
    code: '',
    lot_type: defaultLotType,
  });

  useEffect(() => {
    if (open) {
      setError('');
      setSelectedAnimalIds([]);
      setForm({ name: '', code: '', lot_type: defaultLotType });
    }
  }, [open, defaultLotType]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const res = await createLot.mutateAsync({
        name: form.name || null,
        code: form.code || null,
        lot_type: form.lot_type,
      });

      if (selectedAnimalIds.length > 0) {
        await transfer.mutateAsync({
          property_id: propertyId,
          movement_date: todayIso(),
          scope: 'animal',
          notes: `Composição do lote ${res.lot.name}`,
          animal_ids: selectedAnimalIds,
          destination_lot_id: res.lot.id,
          is_off_property: false,
        });
      }

      setForm({ name: '', code: '', lot_type: defaultLotType });
      setSelectedAnimalIds([]);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao criar lote.'));
    }
  }

  const loading = createLot.isPending || transfer.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Novo lote — ${propertyName}`}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-lot-form-modal" loading={loading}>
            {selectedAnimalIds.length > 0 ? 'Criar lote com animais' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id="create-lot-form-modal" onSubmit={handleSubmit} className="space-y-5">
        {error && <p className="text-sm text-red-600">{error}</p>}

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-manejai-forest">Tipo do lote</legend>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {LOT_TYPES.map((type) => {
              const Icon = type.icon;
              const selected = form.lot_type === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  title={type.label}
                  aria-pressed={selected}
                  onClick={() => setForm((f) => ({ ...f, lot_type: type.value }))}
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-2xl border px-2 py-3 text-center transition-colors',
                    selected
                      ? 'border-manejai-forest bg-manejai-forest/5 text-manejai-forest shadow-sm'
                      : 'border-manejai-sand bg-white text-manejai-bark/80 hover:border-manejai-sage hover:bg-manejai-cream/40',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-xl',
                      selected ? 'bg-manejai-forest text-white' : 'bg-manejai-sand/60 text-manejai-forest',
                    )}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <span className="text-xs font-medium leading-tight">{type.shortLabel}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <Input
          label="Nome"
          required
          placeholder="Ex.: Pasto 1"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Input
          label="Código"
          placeholder="Opcional"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        />

        <LotAnimalPicker
          propertyId={propertyId}
          selectedIds={selectedAnimalIds}
          onChange={setSelectedAnimalIds}
          optional
        />
      </form>
    </Modal>
  );
}
